import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  Divider,
  Chip,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon,
  BarChart as BarChartIcon,
  Person as PersonIcon,
  CompareArrows as CompareArrowsIcon,
} from "@mui/icons-material";
import { useApp } from "../context/AppContext";
import { useThemeColors } from "../hooks/useThemeColors";
import { meses, anos } from "../data/mockData";

function ComparativoVendas() {
  const { corretores, getMetaPorAno } = useApp();
  const colors = useThemeColors();

  const [corretorSelecionado, setCorretorSelecionado] = useState("");
  const [mesSelecionado, setMesSelecionado] = useState("Todos");
  const [tipoComparacao, setTipoComparacao] = useState("todos"); // 'todos' ou 'filtrar'
  const [anosSelecionados, setAnosSelecionados] = useState([2024, 2025, 2026]);

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);
  };

  // Filtrar apenas corretores ativos
  const corretoresAtivos = corretores.filter((c) => c.ativo);

  // Dados do comparativo mês a mês
  const dadosComparativo = useMemo(() => {
    if (!corretorSelecionado) return null;

    const corretor = corretoresAtivos.find((c) => c.id === corretorSelecionado);
    if (!corretor) return null;

    // Determinar quais anos usar baseado no tipo de comparação
    const anosParaComparar =
      tipoComparacao === "filtrar" ? anosSelecionados : anos;

    // Helper function to get venda value (backward compatible)
    const getVendaValor = (corretor, ano, mes) => {
      const venda = corretor.vendas?.[ano]?.[mes];
      if (!venda) return 0;
      // Support both old format (number) and new format (object)
      return typeof venda === 'object' ? (venda.valor || 0) : venda;
    };

    // Buscar vendas do mês selecionado ou total do ano se "Todos" estiver selecionado
    const comparativoPorAno = anosParaComparar
      .map((ano) => {
        let vgv = 0;
        if (mesSelecionado === "Todos") {
          // Calcular VGV total do ano (soma de todos os meses)
          meses.forEach((mes) => {
            vgv += getVendaValor(corretor, ano, mes);
          });
        } else {
          vgv = getVendaValor(corretor, ano, mesSelecionado);
        }
        return { ano, vgv };
      })
      .filter(
        (item) =>
          item.vgv > 0 ||
          anosParaComparar.indexOf(item.ano) === anosParaComparar.length - 1
      ); // Mostrar pelo menos o último ano

    // Calcular variações ano a ano e percentual da meta
    const comparativoComVariacao = comparativoPorAno.map((item, index) => {
      const metaAno = getMetaPorAno(item.ano);
      const percentualMeta = metaAno > 0 ? (item.vgv / metaAno) * 100 : 0;

      if (index === 0) {
        return {
          ...item,
          variacao: 0,
          variacaoPercentual: 0,
          percentualMeta,
          metaAno,
        };
      }

      const anoAnterior = comparativoPorAno[index - 1];
      const variacao = item.vgv - anoAnterior.vgv;
      const variacaoPercentual =
        anoAnterior.vgv > 0
          ? ((item.vgv - anoAnterior.vgv) / anoAnterior.vgv) * 100
          : item.vgv > 0
          ? 100
          : 0;

      return { ...item, variacao, variacaoPercentual, percentualMeta, metaAno };
    });

    // Calcular totais e médias
    const totalGeral = comparativoPorAno.reduce(
      (sum, item) => sum + item.vgv,
      0
    );
    const mediaAnual =
      comparativoPorAno.length > 0 ? totalGeral / comparativoPorAno.length : 0;
    const melhorAno = comparativoPorAno.reduce(
      (max, item) => (item.vgv > max.vgv ? item : max),
      comparativoPorAno[0]
    );
    const piorAno = comparativoPorAno.reduce(
      (min, item) => (item.vgv < min.vgv || min.vgv === 0 ? item : min),
      comparativoPorAno[0]
    );

    // Preparar dados mensais para comparação
    const comparativoMensal = meses.map((mes) => {
      const dadosMes = anosParaComparar.map((ano) => {
        const vgv = getVendaValor(corretor, ano, mes);
        return { ano, vgv };
      });
      return { mes, dados: dadosMes };
    });

    return {
      corretor,
      comparativoPorAno: comparativoComVariacao,
      totalGeral,
      mediaAnual,
      melhorAno,
      piorAno,
      comparativoMensal,
      anosParaComparar,
    };
  }, [
    corretorSelecionado,
    mesSelecionado,
    corretoresAtivos,
    tipoComparacao,
    anosSelecionados,
  ]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <CompareArrowsIcon sx={{ color: colors.primary, fontSize: 32 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.primary} ${colors.secondary}`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Comparativo de Vendas Por Corretor
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          Análise comparativa do mesmo mês em diferentes anos
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          background: colors.background.paper,
          border: `1px solid ${colors.border.primary}`,
          borderRadius: 3,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Corretor</InputLabel>
              <Select
                value={corretorSelecionado}
                label="Corretor"
                onChange={(e) => setCorretorSelecionado(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border.primary,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Selecione um corretor</em>
                </MenuItem>
                {corretoresAtivos.map((corretor) => (
                  <MenuItem key={corretor.id} value={corretor.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar
                        src={corretor.foto || undefined}
                        alt={corretor.nome}
                        sx={{ width: 24, height: 24 }}
                      >
                        {!corretor.foto && corretor.nome
                          ? corretor.nome
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .substring(0, 2)
                              .toUpperCase()
                          : ""}
                      </Avatar>
                      {corretor.nome}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Mês</InputLabel>
              <Select
                value={mesSelecionado}
                label="Mês"
                onChange={(e) => setMesSelecionado(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border.primary,
                  },
                }}
              >
                <MenuItem value="Todos">
                  <em>Todos</em>
                </MenuItem>
                {meses.map((mes) => (
                  <MenuItem key={mes} value={mes}>
                    {mes}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "text.primary", mr: 1 }}
              >
                Tipo de Comparação:
              </Typography>
              <RadioGroup
                row
                value={tipoComparacao}
                onChange={(e) => {
                  setTipoComparacao(e.target.value);
                  if (e.target.value === "todos") {
                    setAnosSelecionados([2024, 2025, 2026]);
                  }
                }}
                sx={{ flex: 1 }}
              >
                <FormControlLabel
                  value="todos"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: colors.primary,
                        "&.Mui-checked": { color: colors.primary },
                      }}
                    />
                  }
                  label="Ano a Ano"
                  sx={{ mr: 1 }}
                />
                <FormControlLabel
                  value="filtrar"
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: colors.primary,
                        "&.Mui-checked": { color: colors.primary },
                      }}
                    />
                  }
                  label="Escolher Ano"
                />
              </RadioGroup>
            </Box>
          </Grid>
          {tipoComparacao === "filtrar" && (
            <Grid item xs={12} sm={12} md={3}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: "0.875rem",
                  }}
                >
                  Anos:
                </Typography>
                {anos.map((ano) => (
                  <FormControlLabel
                    key={ano}
                    control={
                      <Checkbox
                        size="small"
                        checked={anosSelecionados.includes(ano)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAnosSelecionados(
                              [...anosSelecionados, ano].sort()
                            );
                          } else {
                            setAnosSelecionados(
                              anosSelecionados.filter((a) => a !== ano)
                            );
                          }
                        }}
                        sx={{
                          color: colors.primary,
                          "&.Mui-checked": {
                            color: colors.primary,
                          },
                        }}
                      />
                    }
                    label={ano}
                    sx={{
                      margin: 0,
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.875rem",
                        color: "text.primary",
                      },
                    }}
                  />
                ))}
              </Box>
              {anosSelecionados.length === 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "error",
                    mt: 0.5,
                    display: "block",
                    fontSize: "0.75rem",
                  }}
                >
                  Selecione pelo menos um ano
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>

      {!dadosComparativo ? (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            background: colors.background.paper,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: 3,
          }}
        >
          <PersonIcon
            sx={{ fontSize: 64, color: "text.secondary", mb: 2, opacity: 0.5 }}
          />
          <Typography variant="h6" sx={{ color: "text.secondary", mb: 1 }}>
            Selecione um corretor e um mês para visualizar o comparativo
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Escolha um corretor e um mês para ver a análise comparativa entre os
            anos
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Informações do Corretor */}
          <Paper
            sx={{
              p: 3,
              mb: 3,
              background: colors.background.paper,
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Avatar
                src={dadosComparativo.corretor.foto || undefined}
                alt={dadosComparativo.corretor.nome}
                sx={{
                  width: 64,
                  height: 64,
                  border: `2px solid ${colors.primary}`,
                }}
              >
                {!dadosComparativo.corretor.foto &&
                dadosComparativo.corretor.nome
                  ? dadosComparativo.corretor.nome
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .substring(0, 2)
                      .toUpperCase()
                  : ""}
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "text.primary" }}
                >
                  {dadosComparativo.corretor.nome}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Comparativo{" "}
                  {mesSelecionado === "Todos"
                    ? "Anual"
                    : `de ${mesSelecionado}`}{" "}
                  entre os anos
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
          </Paper>

          {/* Cards de Resumo */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}08 100%)`,
                  border: `1px solid ${colors.primary}40`,
                  borderRadius: 3,
                  p: 2,
                  height: "100%",
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Total Geral
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: colors.primary, mt: 1 }}
                  >
                    {formatarMoeda(dadosComparativo.totalGeral)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", mt: 1, display: "block" }}
                  >
                    Soma de todos os anos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}08 100%)`,
                  border: `1px solid ${colors.primary}40`,
                  borderRadius: 3,
                  p: 2,
                  height: "100%",
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Média Anual
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: colors.primary, mt: 1 }}
                  >
                    {formatarMoeda(dadosComparativo.mediaAnual)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", mt: 1, display: "block" }}
                  >
                    Média entre os anos
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}08 100%)`,
                  border: `1px solid ${colors.primary}40`,
                  borderRadius: 3,
                  p: 2,
                  height: "100%",
                  transition: "all 0.3s ease",
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Melhor Ano
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#4CAF50", mt: 1 }}
                  >
                    {dadosComparativo.melhorAno.ano}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", mt: 1, display: "block" }}
                  >
                    {formatarMoeda(dadosComparativo.melhorAno.vgv)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary}15 0%, ${colors.primary}08 100%)`,
                  border: `1px solid #F4433640`,
                  borderRadius: 3,
                  p: 2,
                  height: "100%",
                  transition: "all 0.3s ease",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background: "#F44336",
                  },
                }}
              >
                <CardContent>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    Pior Ano
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: "#F44336", mt: 1 }}
                  >
                    {dadosComparativo.piorAno.ano}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", mt: 1, display: "block" }}
                  >
                    {formatarMoeda(dadosComparativo.piorAno.vgv)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Comparativo Ano a Ano */}
          <Paper
            sx={{
              background: colors.background.paper,
              border: `1px solid ${colors.border.primary}`,
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${colors.border.primary}`,
                background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}08 100%)`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <BarChartIcon sx={{ color: colors.primary }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  Comparativo{" "}
                  {mesSelecionado === "Todos" ? "Anual" : mesSelecionado} - Ano
                  a Ano
                </Typography>
              </Box>
            </Box>
            <Box sx={{ p: 3 }}>
              <Grid
                container
                spacing={3}
                sx={{ display: "flex", alignItems: "stretch" }}
              >
                {dadosComparativo.comparativoPorAno.map((item, index) => {
                  const isPrimeiro = index === 0;
                  const isCrescimento = item.variacao > 0;
                  const isQueda = item.variacao < 0;
                  const isEstavel = item.variacao === 0;
                  const percentualMaximo =
                    dadosComparativo.melhorAno.vgv > 0
                      ? (item.vgv / dadosComparativo.melhorAno.vgv) * 100
                      : 0;

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={item.ano}
                      sx={{ display: "flex" }}
                    >
                      <Card
                        sx={{
                          background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.background.default} 100%)`,
                          border: `2px solid ${
                            isPrimeiro
                              ? colors.border.primary
                              : isCrescimento
                              ? "#4CAF5040"
                              : isQueda
                              ? "#F4433640"
                              : colors.border.primary
                          }`,
                          borderRadius: 3,
                          p: 3,
                          position: "relative",
                          overflow: "hidden",
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "4px",
                            background: isPrimeiro
                              ? colors.primary
                              : isCrescimento
                              ? "#4CAF50"
                              : isQueda
                              ? "#F44336"
                              : colors.border.primary,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 2,
                            flex: 1,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: 1,
                                mb: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="h4"
                                sx={{ fontWeight: 700, color: "text.primary" }}
                              >
                                {item.ano}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.875rem",
                                }}
                              >
                                Meta: {formatarMoeda(item.metaAno)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: colors.primary,
                                mt: 1,
                              }}
                            >
                              {formatarMoeda(item.vgv)}
                            </Typography>
                            <Chip
                              label={`${item.percentualMeta.toFixed(
                                1
                              )}% da meta`}
                              size="small"
                              sx={{
                                mt: 1,
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                color: "#ffffff",
                                border: "1px solid rgba(255, 255, 255, 0.3)",
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                height: 24,
                              }}
                            />
                          </Box>
                          {!isPrimeiro && (
                            <Chip
                              icon={
                                isCrescimento ? (
                                  <TrendingUpIcon sx={{ fontSize: 18 }} />
                                ) : isQueda ? (
                                  <TrendingDownIcon sx={{ fontSize: 18 }} />
                                ) : (
                                  <RemoveIcon sx={{ fontSize: 18 }} />
                                )
                              }
                              label={
                                isCrescimento
                                  ? `+${item.variacaoPercentual.toFixed(1)}%`
                                  : isQueda
                                  ? `${item.variacaoPercentual.toFixed(1)}%`
                                  : "0%"
                              }
                              size="medium"
                              sx={{
                                backgroundColor: isCrescimento
                                  ? "#4CAF5020"
                                  : isQueda
                                  ? "#F4433620"
                                  : colors.background.default,
                                color: isCrescimento
                                  ? "#4CAF50"
                                  : isQueda
                                  ? "#F44336"
                                  : "text.secondary",
                                border: `2px solid ${
                                  isCrescimento
                                    ? "#4CAF5040"
                                    : isQueda
                                    ? "#F4433640"
                                    : colors.border.primary
                                }`,
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                height: 36,
                              }}
                            />
                          )}
                        </Box>

                        {/* Barra de Progresso */}
                        <Box sx={{ mt: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.7rem",
                              }}
                            >
                              Comparado ao melhor ano
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.7rem",
                                fontWeight: 600,
                              }}
                            >
                              {percentualMaximo.toFixed(1)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(percentualMaximo, 100)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor:
                                colors.progress?.background ||
                                `${colors.primary}20`,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: isCrescimento
                                  ? "#4CAF50"
                                  : isQueda
                                  ? "#F44336"
                                  : colors.primary,
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>

                        {!isPrimeiro && (
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: `1px solid ${colors.border.primary}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                                display: "block",
                              }}
                            >
                              Variação: {isCrescimento ? " + " : " "}
                              <b
                                style={{
                                  color: isCrescimento ? "#4CAF50" : "#F44336",
                                }}
                              >
                                {formatarMoeda(item.variacao)}
                              </b>{" "}
                              em relação a{" "}
                              {
                                dadosComparativo.comparativoPorAno[index - 1]
                                  .ano
                              }
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.75rem",
                                display: "block",
                                mt: 0.5,
                              }}
                            ></Typography>
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            
            {/* Comparativo Mensal */}
            {mesSelecionado === "Todos" && (
              <Box
                sx={{
                  borderTop: `1px solid ${colors.border.primary}`,
                  p: 3,
                  background: colors.background.default,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <BarChartIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  Visão Geral Mensal
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            borderBottom: `2px solid ${colors.border.primary}`,
                          }}
                        >
                          Mês
                        </TableCell>
                        {dadosComparativo.anosParaComparar.map((ano) => (
                          <TableCell
                            key={ano}
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: "text.primary",
                              borderBottom: `2px solid ${colors.border.primary}`,
                            }}
                          >
                            {ano}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dadosComparativo.comparativoMensal.map((item) => {
                        const melhorAnoMes = item.dados.reduce(
                          (max, d) => (d.vgv > max.vgv ? d : max),
                          item.dados[0]
                        );

                        return (
                          <TableRow
                            key={item.mes}
                            sx={{
                              "&:hover": {
                                backgroundColor: `${colors.primary}08`,
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 600,
                                color: "text.primary",
                              }}
                            >
                              {item.mes}
                            </TableCell>
                            {item.dados.map((dado) => {
                              const isMelhor = dado.vgv === melhorAnoMes.vgv && dado.vgv > 0;
                              return (
                                <TableCell
                                  key={dado.ano}
                                  align="right"
                                  sx={{
                                    color: isMelhor ? colors.primary : "text.primary",
                                    fontWeight: isMelhor ? 700 : 400,
                                  }}
                                >
                                  {formatarMoeda(dado.vgv)}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                      <TableRow
                        sx={{
                          backgroundColor: `${colors.primary}10`,
                          "& td": {
                            fontWeight: 700,
                            borderTop: `2px solid ${colors.border.primary}`,
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                          Total
                        </TableCell>
                        {dadosComparativo.anosParaComparar.map((ano) => {
                          const totalAno = dadosComparativo.comparativoMensal.reduce(
                            (sum, item) => {
                              const vgvAno = item.dados.find((d) => d.ano === ano)?.vgv || 0;
                              return sum + vgvAno;
                            },
                            0
                          );
                          return (
                            <TableCell
                              key={ano}
                              align="right"
                              sx={{ fontWeight: 700, color: colors.primary }}
                            >
                              {formatarMoeda(totalAno)}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}

export default ComparativoVendas;
